/* ==============================================================================
   Cliente da API
   ==============================================================================
   Uma camada fina sobre `fetch`, com três responsabilidades e nenhuma a mais:

     1. anexar o access token e renovar quando expira
     2. transformar erro HTTP em um erro TIPADO, com o código do servidor
     3. tipar a resposta

   O que ele deliberadamente NÃO faz
   =================================
   Não guarda cache, não gerencia estado de UI, não faz retry automático de
   escrita. Retry de POST é o caminho para duplicar mensagem, e cache de
   permissão é o caminho para "removi o cargo e a pessoa continua entrando".

   O erro é uma CLASSE, não uma string
   ===================================
   `ApiError` carrega `status` e `code`. É isso que permite ao chamador
   distinguir "não tenho permissão" de "o servidor caiu" sem comparar texto de
   mensagem — comparação de texto quebra na primeira tradução, e mensagem é para
   humano, código é para código.
   ============================================================================== */

/** Erro devolvido pela API, com o código de máquina preservado. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** Código estável do servidor: `forbidden`, `rate_limited`, `not_found`… */
    readonly code: string,
    message: string,
    /** Detalhes de validação, quando houver. */
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** `true` se repetir a mesma requisição pode dar outro resultado. */
  get retryable(): boolean {
    // 408, 429 e 5xx são transitórios. 4xx restante é o pedido que está errado,
    // e repetir vai falhar igual.
    return this.status === 408 || this.status === 429 || this.status >= 500;
  }
}

/** Erro de rede: nem chegou a haver resposta HTTP. */
export class NetworkError extends Error {
  /*
   * `override` porque `Error.cause` existe desde ES2022.
   *
   * Sem o modificador, o TypeScript recusa a redeclaração — e com razão: o
   * campo já existe na classe base, e sombreá-lo por acidente descartaria a
   * cadeia de causa que `new Error(msg, { cause })` monta.
   */
  override readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export interface TokenProvider {
  /** Devolve um access token válido, renovando se necessário. */
  getToken: () => Promise<string | null>;
  /** Chamado em 401 depois de uma tentativa de renovação. */
  onUnauthorized: () => void;
}

export interface ApiClientOptions {
  /** Base da API. Padrão `/api/v1`, que funciona quando o front é servido junto. */
  baseUrl?: string;
  tokens: TokenProvider;
  /** Injetável para teste. */
  fetchImpl?: typeof fetch;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Aborta a requisição. */
  signal?: AbortSignal;
  /** Rotas públicas (prévia de convite) não mandam token. */
  anonymous?: boolean;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly tokens: TokenProvider;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions) {
    this.baseUrl = (options.baseUrl ?? '/api/v1').replace(/\/+$/, '');
    this.tokens = options.tokens;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const url = this.buildUrl(path, options.query);

    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers['content-type'] = 'application/json';

    if (!options.anonymous) {
      const token = await this.tokens.getToken();
      if (token === null) {
        this.tokens.onUnauthorized();
        throw new ApiError(401, 'unauthorized', 'Sessão expirada. Entre novamente.');
      }
      headers.authorization = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method: options.method ?? 'GET',
        headers,
        // `same-origin` e não `include`: o cookie de refresh é da mesma origem,
        // e `include` mandaria credencial para terceiros se a base mudasse.
        credentials: 'same-origin',
        ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
        ...(options.signal !== undefined ? { signal: options.signal } : {}),
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new NetworkError('Não foi possível contatar o servidor.', error);
    }

    /*
     * 401 recebe UMA nova tentativa, e só uma.
     *
     * A primeira cobre o caso de o token ter sido invalidado do lado do servidor
     * (troca de senha, revogação) — que a checagem de expiração local não prevê.
     * Repetir em laço num 401 persistente produziria requisição infinita, e é
     * exatamente o que acontece quando a sessão foi revogada de verdade.
     */
    if (response.status === 401 && !options.anonymous) {
      const renewed = await this.tokens.getToken();
      if (renewed === null) {
        this.tokens.onUnauthorized();
        throw new ApiError(401, 'unauthorized', 'Sessão expirada. Entre novamente.');
      }
      headers.authorization = `Bearer ${renewed}`;
      try {
        response = await this.fetchImpl(url, {
          method: options.method ?? 'GET',
          headers,
          credentials: 'same-origin',
          ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
          ...(options.signal !== undefined ? { signal: options.signal } : {}),
        });
      } catch (error) {
        throw new NetworkError('Não foi possível contatar o servidor.', error);
      }
      if (response.status === 401) {
        this.tokens.onUnauthorized();
      }
    }

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const payload: unknown = text.length > 0 ? safeJson(text) : undefined;

    if (!response.ok) {
      const shape = payload as
        | { error?: string; message?: string; details?: unknown }
        | undefined;
      throw new ApiError(
        response.status,
        shape?.error ?? `http_${response.status}`,
        shape?.message ?? `A API respondeu ${response.status}.`,
        shape?.details,
      );
    }

    return payload as T;
  }

  private buildUrl(
    path: string,
    query?: Record<string, string | number | boolean | undefined>,
  ): string {
    const url = `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
    if (!query) return url;

    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      // `undefined` é omitido; `null` não é aceito pelo tipo de propósito. A
      // diferença importa: `?before=undefined` chegaria como a string
      // "undefined" e o servidor a rejeitaria como cursor inválido.
      if (value !== undefined) params.set(key, String(value));
    }
    const qs = params.toString();
    return qs.length > 0 ? `${url}?${qs}` : url;
  }

  get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) {
    return this.request<T>(path, { ...options, method: 'GET' });
  }
  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}) {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }
  patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}) {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }
  put<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}) {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }
  delete<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // Resposta não-JSON num erro é comum: nginx devolvendo HTML de 502, por
    // exemplo. Devolver o texto cru deixa a mensagem útil em vez de "JSON
    // inválido", que esconderia a causa real.
    return { error: 'invalid_response', message: text.slice(0, 200) };
  }
}
